import _ from 'lodash'
import { allMutations, allQueries, PRIMITIVE_TYPES, SIMPLE_TYPES, TYPE_NAME_TO_MODEL_MAP } from 'src/models/model/constants'
import Model from 'src/models/model/model'

/**
 * The current local id. Local ids are numbers less than 0.
 * @type {number}
 */
let localId = -1

/**
 * Get the next local id. Each local id is unique.
 * @returns {number} The next local id.
 */
export function nextLocalId () {
  return localId--
}

/**
 * The current unique session identifier.
 * @type {number}
 */
let sessionIdentifier = Math.round(Math.random() * 100000000)

export function getCurrentSessionIdentifier () {
  return sessionIdentifier
}

export function resetSessionIdentifier () {
  sessionIdentifier = Math.round(Math.random() * 100000000)
}

/**
 * Register a model class in the global model map, and register its queries and
 * mutations in the global queries and mutations maps.
 * @param {Promise<Model|{default: Model}>} Type - A promise that resolves to a model class or
 * a module that exports a model class. E.g. the dynamic import of a model file.
 */
export async function registerModel (Type) {
  await Promise.resolve(Type).then(modelOrModule => {
    /** @type {Model} */
    const Type = modelOrModule?.default || modelOrModule

    TYPE_NAME_TO_MODEL_MAP[Type.name] = Type

    Type.staticInitialization()

    registerQueries(Type.queries)
    registerMutations(Type.mutations)

    if (Type.isConfiguration) {
      Model.ConfigurationType = Type
    }
    if (Type.isContext) {
      Model.ContextType = Type
    }
  })
}

/**
 * Register a set of queries in the global queries map.
 * @param {Record<string, Record<string, any>>} queries - A map of query names to their definitions.
 */
export function registerQueries (queries) {
  _.each(queries, (value, name) => {
    if (allQueries[name]) {
      throw new Error('A query with this name is already defined: ' + name)
    }

    allQueries[name] = value
  })
}

/**
 * Register a set of mutations in the global mutations map.
 * @param {Record<string, Record<string, any>>} mutations - A map of mutation names to their definitions.
 */
export function registerMutations (mutations) {
  _.each(mutations, (value, name) => {
    if (allMutations[name]) {
      throw new Error('A mutation with this name is already defined: ' + name)
    }

    allMutations[name] = value
  })
}

/**
 * Check if a model is a primitive type.
 * @param {abstract new (...args: any[]) => any} Model - A constructor function (class).
 * @returns {boolean} True if the model is a primitive type, false otherwise.
 */
export function isPrimitive (Model) {
  return _.includes(PRIMITIVE_TYPES, Model.name)
}

/**
 * Check if a model is a simple type.
 * @param {abstract new (...args: any[]) => any} Model - A constructor function (class).
 * @returns {boolean} True if the model is a simple type, false otherwise.
 */
export function isSimpleType (Model) {
  return _.includes(SIMPLE_TYPES, Model.name)
}
